import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2, Youtube, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WABroadcastButton from "@/components/WABroadcastButton";
import SocialSharePanel from "@/components/SocialSharePanel";

export default function NewsListPage() {
  const [items, setItems] = useState([]);
  const [ytOpen, setYtOpen] = useState(false);
  const [ytUrls, setYtUrls] = useState("");
  const [ytCat, setYtCat] = useState("वीडियो");
  const [ytBusy, setYtBusy] = useState(false);
  const [shortBusy, setShortBusy] = useState(null);      // slug currently generating
  const [shortDialog, setShortDialog] = useState(null);  // { slug, doc }
  const load = () => api.get("/news", { params: { published_only: false, limit: 200 } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("क्या आप वाकई इस समाचार को हटाना चाहते हैं?")) return;
    try { await api.delete(`/news/${id}`); toast.success("हटा दिया गया"); load(); }
    catch(e) { toast.error(formatError(e)); }
  };

  const ytImport = async () => {
    const urls = ytUrls.split(/\s+/).filter(u => u.trim().length > 0);
    if (urls.length === 0) return toast.error("कम से कम एक URL डालें");
    setYtBusy(true);
    try {
      const { data } = await api.post("/news/import-youtube", { items: urls.map(u => ({ video_url: u.trim(), category: ytCat })) });
      toast.success(`${data.imported} वीडियो समाचार आयातित`);
      setYtOpen(false); setYtUrls(""); load();
    } catch(err) { toast.error(formatError(err)); }
    finally { setYtBusy(false); }
  };

  const genShort = async (n) => {
    if (!window.confirm(`YouTube Short generate करें? इसमें 30-60 सेकंड लगेंगे।\n\nTitle: ${n.title}`)) return;
    setShortBusy(n.slug);
    try {
      const { data } = await api.post(`/admin/news/${n.slug}/generate-short`, new URLSearchParams({ voice: "onyx", duration: "60" }), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" }, timeout: 300000,
      });
      toast.success(`Short ready — ${data.size_mb}MB · ${data.duration}s`);
      setShortDialog({ slug: n.slug, doc: data, title: n.title });
    } catch (err) { toast.error(formatError(err)); }
    finally { setShortBusy(null); }
  };

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div data-testid="news-list-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">समाचार प्रबंधन</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {items.length} समाचार</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={ytOpen} onOpenChange={setYtOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" data-testid="yt-import-btn"><Youtube className="w-4 h-4 mr-1.5"/>YouTube आयात</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>YouTube वीडियो आयात करें</DialogTitle>
                <DialogDescription>एक या अधिक YouTube URL डालें (हर URL नयी पंक्ति पर) — शीर्षक और थंबनेल स्वचालित रूप से भरे जायेंगे</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>श्रेणी</Label><Input value={ytCat} onChange={(e) => setYtCat(e.target.value)}/></div>
                <div>
                  <Label>YouTube URLs</Label>
                  <Textarea rows={7} value={ytUrls} onChange={(e) => setYtUrls(e.target.value)}
                    placeholder={"https://youtu.be/xxxxxxxxxxx\nhttps://www.youtube.com/watch?v=yyyyyyyyyyy"}
                    className="font-mono text-xs" data-testid="yt-urls"/>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setYtOpen(false)}>रद्द</Button>
                <Button onClick={ytImport} disabled={ytBusy} className="bg-red-600 hover:bg-red-700" data-testid="yt-import-submit">{ytBusy ? "आयात हो रहा…" : "आयात करें"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Link to="/admin/news/new"><Button className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="add-news-btn"><Plus className="w-4 h-4 mr-1.5"/>नया समाचार</Button></Link>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>शीर्षक</TableHead>
              <TableHead>श्रेणी</TableHead>
              <TableHead>लेखक</TableHead>
              <TableHead>स्थिति</TableHead>
              <TableHead>व्यू</TableHead>
              <TableHead className="text-right">कार्य</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((n) => (
              <TableRow key={n.id} data-testid={`news-row-${n.id}`}>
                <TableCell className="font-medium max-w-md truncate">{n.title}</TableCell>
                <TableCell>{n.category}</TableCell>
                <TableCell>{n.author}</TableCell>
                <TableCell>
                  {n.is_breaking && <Badge className="bg-[#DC2626] mr-1">ब्रेकिंग</Badge>}
                  {n.is_editorial && <Badge className="bg-gradient-to-r from-[#EA580C] to-[#DC2626] mr-1">✦ संपादकीय</Badge>}
                  {n.video_url && <Badge variant="outline" className="mr-1 text-red-600 border-red-300"><Youtube className="w-3 h-3 mr-0.5"/>वीडियो</Badge>}
                  <Badge variant={n.is_published ? "default" : "secondary"}>{n.is_published ? "प्रकाशित" : "ड्राफ्ट"}</Badge>
                </TableCell>
                <TableCell>{n.views || 0}</TableCell>
                <TableCell className="text-right">
                  {n.is_published && (
                    <SocialSharePanel slug={n.slug} title={n.title} className="mr-1 h-8 px-2 text-xs"/>
                  )}
                  {n.is_published && (
                    <Button variant="ghost" size="sm" disabled={shortBusy === n.slug}
                      onClick={() => genShort(n)}
                      title="60-sec Hindi voice-over vertical Short generate करें"
                      className="text-fuchsia-600 hover:bg-fuchsia-50 mr-1"
                      data-testid={`yt-short-${n.id}`}>
                      {shortBusy === n.slug ? <Loader2 className="w-4 h-4 animate-spin"/> : <Film className="w-4 h-4"/>}
                    </Button>
                  )}
                  {n.is_breaking && <WABroadcastButton title={n.title} articleUrl={`${window.location.origin}/article/${n.slug}`} className="mr-1"/>}
                  <Link to={`/admin/news/${n.id}/edit`}><Button variant="ghost" size="sm" data-testid={`edit-news-${n.id}`}><Edit2 className="w-4 h-4"/></Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => del(n.id)} data-testid={`del-news-${n.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">कोई समाचार नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* YouTube Short preview dialog */}
      <Dialog open={!!shortDialog} onOpenChange={(v) => !v && setShortDialog(null)}>
        <DialogContent className="max-w-md" data-testid="short-preview-dialog">
          <DialogHeader>
            <DialogTitle>🎬 Short तैयार है</DialogTitle>
            <DialogDescription className="line-clamp-2">{shortDialog?.title}</DialogDescription>
          </DialogHeader>
          {shortDialog && (
            <div className="space-y-3">
              <video src={`${backendUrl}${shortDialog.doc.url}`} controls playsInline
                     className="w-full rounded-lg bg-black aspect-[9/16] max-h-[70vh] mx-auto"
                     data-testid="short-preview-video"/>
              <div className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg leading-relaxed">
                <div className="font-black text-slate-900 mb-1">Script:</div>
                {shortDialog.doc.script}
              </div>
              <div className="text-[11px] text-slate-500">Size: <b>{shortDialog.doc.size_mb}MB</b> · Duration: <b>{shortDialog.doc.duration}s</b> · Voice: <b>{shortDialog.doc.voice}</b></div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <a href={shortDialog ? `${backendUrl}${shortDialog.doc.url}` : "#"} download
               className="inline-flex items-center justify-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black py-2 px-4 rounded flex-1"
               data-testid="short-download">
              <Film className="w-4 h-4"/> Download MP4
            </a>
            <a href="https://www.youtube.com/upload" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-black py-2 px-4 rounded flex-1"
               data-testid="short-upload">
              <Youtube className="w-4 h-4"/> YouTube पर Upload
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
